import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import { ParcelOrderList } from "Components/order-management/parcel-order-list";
import { Radio, Tooltip } from "antd";
import PickupModal from "Components/order-management/shared/parcel-modals/pickup-create";
import ParcelDropoffModal from "Components/order-management/shared/parcel-modals/dropoff-create";
import { Button, BasicModal } from "@life-line-lab/components-react";
import { useHistory } from "react-router-dom";
import useQuery from "Hooks/query.hook";
import { QuestionCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import { useOrderListingStates } from "Helpers/index";
import useCorporateType from "Hooks/corporate-type.hook";

const DispatchReadyTab = ({
  handleFetchServicePoints,
  getLocationList,
  getLocationListForModal,
  getWorkingHours,
  baseColumns,
  handleRefreshData,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const query = useQuery();
  const [radioButtonState, setRadioButtonState] = useState("all");
  const {
    selectedRows,
    setSelectedRows,
    workingHours,
    filterObj,
    setFilterObj,
    dropOffLocationList,
    initialFilters,
    customersList,
  } = useOrderListingStates();
  const { isLogisticProvider } = useCorporateType();
  const tabKey = query.get("tabKey") || 3;

  useEffect(() => {
    handleRadioChangeFilter();
    handleFetchServicePoints({ type: "STATION", active: true });
  }, [radioButtonState]);

  useEffect(() => {
    if (!isEmpty(filterObj.customersList)) {
      getLocationList(filterObj.customersList);
    }
  }, [filterObj.customersList]);

  useEffect(() => {
    if (!isEmpty(filterObj.locationID)) {
      getWorkingHours(filterObj.locationID);
    }
  }, [filterObj.locationID]);

  const handleRowSelection = (record: any) => {
    if (!record._id) return false;

    const sortingHubID = record?.midMileInfoData?.sortingHub?.id;

    if (!sortingHubID) return false;
    return true;
  };

  const handleActionClick = () => {
    setModal({ open: true });
  };

  const [callModal, setCallModal] = useState<any>(<></>);

  const initialModal: any = {
    open: false,
  };

  const [modal, setModal] = useState<any>(initialModal);

  const handleResetModalState = () => {
    setModal(initialModal);
  };

  const handleRadioChangeFilter = () => {
    setSelectedRows && setSelectedRows([]);
    switch (radioButtonState) {
      case "all":
        setFilterObj({
          ...initialFilters,
          orderStatus: ["PENDING_PICKUP", "PENDING_DROP_OFF"],
        });
        history.push(
          `/parcels-order-processing/parcels?tabKey=${tabKey}&page=1`,
        );
        break;
      case "pickup":
        setFilterObj({
          ...initialFilters,
          orderStatus: ["PENDING_PICKUP"],
        });
        history.push(
          `/parcels-order-processing/parcels?tabKey=${tabKey}&subTab=pickup&page=1`,
        );
        break;
      case "dropoff":
        setFilterObj({
          ...initialFilters,
          orderStatus: ["PENDING_DROP_OFF"],
        });
        history.push(
          `/parcels-order-processing/parcels?tabKey=${tabKey}&subTab=dropoff&page=1`,
        );
        break;
      default:
        break;
    }
  };

  const handleSubmit = (type, data) => {
    setSelectedRows && setSelectedRows([]);
    onSubmit(type, data);
  };

  const handleModalAction = async (action: string) => {
    let newInfo: any = {
      selectedOrders: selectedRows,
      workingHours,
      selectedDeliveryDate: filterObj.deliveryDate,
      filtersFlag: false,
    };
    if (!isEmpty(filterObj.customersList) && !isEmpty(filterObj.locationID)) {
      newInfo = { ...newInfo, filtersFlag: true };
    }

    if (action === "pickup") {
      setCallModal(
        <PickupModal
          info={newInfo}
          dropOffLocationList={dropOffLocationList}
          onSubmit={handleSubmit}
          onCancel={handleResetModalState}
          customerList={customersList}
          getLocationListForModal={getLocationListForModal}
        />,
      );
      handleActionClick();
    } else if (action === "dropoff") {
      setCallModal(
        <ParcelDropoffModal
          info={newInfo}
          dropOffLocationList={dropOffLocationList}
          onSubmit={handleSubmit}
          onCancel={handleResetModalState}
        />,
      );
      handleActionClick();
    }
  };

  const showButtonHandler = () => {
    const disableAction = isEmpty(selectedRows);

    const disableDropOff =
      isEmpty(filterObj.customersList) ||
      isEmpty(filterObj.locationID) ||
      disableAction;

    const PickUpButton = () => {
      return (
        <div className="btn-actions-container">
          <Button
            type="primary"
            label={t("action.createPickUp")}
            onClick={() => {
              handleModalAction("pickup");
            }}
            className="createPickUp-btn"
          />
        </div>
      );
    };

    const DropOffButton = () => {
      return (
        <div className="btn-actions-container">
          <Button
            type="primary"
            label={t("action.createDropOff")}
            isDisabled={disableDropOff}
            onClick={() => {
              handleModalAction("dropoff");
            }}
            className="createDropOff-btn"
          />
        </div>
      );
    };

    return (
      <>
        {radioButtonState !== "all" && (
          <div className="om-put-btn">
            <Tooltip
              title={t(
                radioButtonState === "pickup"
                  ? "txt.pickUpHelp"
                  : "txt.dropOffHelp",
              )}
            >
              <QuestionCircleOutlined style={{ fontSize: "20px" }} />
            </Tooltip>
            {radioButtonState === "pickup" ? (
              <PickUpButton />
            ) : (
              <DropOffButton />
            )}
          </div>
        )}
      </>
    );
  };

  const additionalColumns = [
    {
      title: t("label.pickUpDateAndTime").toLocaleUpperCase(),
      dataIndex: ["firstMileInfoData", "pickupData", "pickupDate"],
      ellipsis: true,
      width: 120,
      render: (text: any) =>
        text ? moment(text).format("DD-MM-YYYY hh:mm a") : null,
    },
  ];

  return (
    <>
      <ParcelOrderList
        columns={baseColumns.concat(additionalColumns)}
        additionalComponent={
          radioButtonState !== "all" &&
          !isLogisticProvider &&
          showButtonHandler()
        }
        disableRowSelection={handleRowSelection}
        filterItems={["customersList", "locationID", "deliveryDate"]}
        isCheckBoxShown={radioButtonState !== "all"}
        radioButtonComponent={
          <div className="radio-buttons">
            <Radio.Group
              defaultValue="all"
              buttonStyle="solid"
              onChange={(event) => {
                setRadioButtonState(event.target.value);
              }}
            >
              <Radio.Button value="all">{t("option.all")}</Radio.Button>
              <Radio.Button value="pickup">
                {t("option.pendingPickUp")}
              </Radio.Button>
              <Radio.Button value="dropoff">
                {t("option.pendingDropOff")}
              </Radio.Button>
            </Radio.Group>
          </div>
        }
        handleRefreshData={handleRefreshData}
      />

      <BasicModal
        className="modal"
        isOpen={modal.open}
        isClosable={true}
        isMaskClosable={false}
        onClose={() => {
          handleResetModalState();
        }}
      >
        {callModal}
      </BasicModal>
    </>
  );
};

export default DispatchReadyTab;
